
import { Component, Prop, Vue } from 'vue-property-decorator';
import ReviewItemTable from '@/components/purchase-order/review/ReviewItemTable.vue';
import { purchaseList, PURCHASE_LIST_UPDATED } from '@/collections/purchase_list';
import { EventBus } from '@/eventBus';
import type { PurchaseListGroup, PurchaseListItem } from '@/types/purchase-list';
import type { InventoryByVariantId } from '@/utils/purchase-order/fetchInventoryByVariantIds';
import type { ReviewLineEdits } from '@/utils/purchase-order/reviewLineEdits';

interface SupplierOption { id: number; name: string; }

@Component({ components: { ReviewItemTable } })
export default class UnassignedReviewCard extends Vue {
  @Prop({ required: true }) public readonly group!: PurchaseListGroup;
  @Prop({ default: () => [] }) public readonly availableSuppliers!: SupplierOption[];
  @Prop({ default: () => ({}) }) public readonly inventoryByVariantId!: InventoryByVariantId;
  @Prop({ default: false }) public readonly inventoryLoading!: boolean;
  @Prop({ default: () => ({}) }) public readonly edits!: ReviewLineEdits;

  public chosenSupplier: SupplierOption | null = null;
  public assigning = false;

  public get canAssign(): boolean {
    return !!this.chosenSupplier && this.group.items.length > 0;
  }

  public async onQtyChange(payload: { item: PurchaseListItem; quantity: number }): Promise<void> {
    const previous = Number(payload.item.quantity);
    payload.item.quantity = payload.quantity;
    try {
      await purchaseList.updateItem(payload.item.id, { quantity: payload.quantity });
    } catch {
      payload.item.quantity = previous;
      EventBus.$emit('show-snackbar', {
        message: 'Failed to update quantity',
        color: 'error',
      });
    }
  }

  public async onRemove(item: PurchaseListItem): Promise<void> {
    const idx = this.group.items.findIndex((i) => i.id === item.id);
    if (idx !== -1) { this.group.items.splice(idx, 1); }
    await purchaseList.removeItem(item.id);
  }

  public async onAssignAll(): Promise<void> {
    if (!this.canAssign || !this.chosenSupplier) { return; }
    const sup = this.chosenSupplier;
    const total = this.group.items.length;
    this.assigning = true;
    try {
      const results = await Promise.allSettled(
        this.group.items.map((item) => purchaseList.assignSupplier(item.id, sup.id, sup.name)),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      EventBus.$emit(PURCHASE_LIST_UPDATED);
      if (succeeded === total) {
        this.$emit('assigned', { supplier: sup, count: total });
        this.chosenSupplier = null;
      } else {
        this.$emit('assign-failed', { supplier: sup, succeeded, total });
      }
    } finally {
      this.assigning = false;
    }
  }
}
